import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Copy, Check, Sparkles, RefreshCw } from 'lucide-react';
import ListenButton from './shared/ListenButton';
import { useApi } from '../hooks/useApi';
import AiDisclaimer from './shared/AiDisclaimer';

const AUDIENCE_TABS = [
  { key: 'ciso',  label: 'CISO'  },
  { key: 'cfo',   label: 'CFO'   },
  { key: 'cio',   label: 'CIO'   },
  { key: 'ceo',   label: 'CEO'   },
  { key: 'board', label: 'Board' },
];

const REASONING_TABS = [
  false && { key: 'counterfactual', label: 'Counterfactual' },
  { key: 'anomaly',        label: 'Anomaly'   },
  { key: 'summary30d',     label: '30-Day'    },
].filter(Boolean);

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  } catch { return ''; }
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard denied */ }
  };
  return (
    <button
      onClick={copy}
      className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors px-2 py-1 rounded hover:bg-gray-100"
      title="Copy to clipboard"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const RansomwareNarrativePanel = ({ assessmentId, canGenerate }) => {
  const apiCall = useApi();
  const [activeTab, setActiveTab] = useState('ciso');
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const cacheRef = useRef({});

  const fetchNarrative = useCallback(async (voice) => {
    if (cacheRef.current[voice]) {
      setData(cacheRef.current[voice]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await apiCall('POST', `/ransomwareAssessments/${assessmentId}/generate-narrative?voice=${voice}`);
      if (result && result.narrative != null) {
        cacheRef.current[voice] = result;
        setData(result);
      } else {
        setData(null);
      }
    } catch (err) {
      setError('Narrative generation failed.');
    } finally {
      setLoading(false);
    }
  }, [assessmentId, apiCall]);

  // Auto-fetch default voice on mount
  useEffect(() => {
    if (assessmentId && canGenerate) {
      fetchNarrative('ciso');
    }
  }, [assessmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabClick = (voice) => {
    setActiveTab(voice);
    if (canGenerate) {
      fetchNarrative(voice);
    }
  };

  const handleRegenerate = () => {
    delete cacheRef.current[activeTab];
    setData(null);
    fetchNarrative(activeTab);
  };

  const activeText   = data?.narrative ?? '';
  const hasNarrative = data != null;
  const generatedAt  = data?.generatedAtUtc;

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-4 overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0" />
          <div>
            <span className="text-base font-semibold text-gray-900">Intelligence Narrative</span>
            <p className="text-xs text-gray-400 mt-0.5">AI-generated briefing from assessment data</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          {/* The saved narrative is served until Regenerate, its first view or a day old, so the
              time it was written is shown rather than a "data updated" prompt. */}
          {generatedAt && (
            <span className="text-xs text-gray-400">Generated {fmtDate(generatedAt)}</span>
          )}
          {canGenerate && <button
            onClick={handleRegenerate}
            disabled={loading}
            className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium transition-colors
              ${loading
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : hasNarrative
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-purple-600 text-white hover:bg-purple-700'}`}
          >
            {loading ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Generating...
              </>
            ) : hasNarrative ? (
              <>
                <RefreshCw className="h-3.5 w-3.5" />
                Regenerate
              </>
            ) : (
              <>
                <Sparkles className="h-3.5 w-3.5" />
                Generate Narrative
              </>
            )}
          </button>}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="px-5 py-3 bg-red-50 border-b border-red-100 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Body — only shown after first generation */}
      {!hasNarrative && !loading && !error && (
        <div className="px-5 py-6 text-center">
          <p className="text-sm text-gray-400">
            Generate an AI-powered briefing tailored for CISO, CFO, and Board audiences.<br />
            Results are cached — subsequent loads are instant.
          </p>
        </div>
      )}

      {loading && !hasNarrative && (
        <div className="px-5 py-6 text-center">
          <RefreshCw className="h-6 w-6 text-purple-400 animate-spin mx-auto mb-2" />
          <p className="text-sm text-gray-400">Generating briefing…</p>
        </div>
      )}

      {(hasNarrative || (loading && hasNarrative)) && (
        <>
          {/* Chrome-style tab bar — Audience Briefings | Analysis */}
          <div style={{ background: '#f1f3f4', borderBottom: '1px solid #e0e0e0', paddingLeft: 12, paddingRight: 12, paddingTop: 8, display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
            {AUDIENCE_TABS.map(tab => {
              const isActive = activeTab === tab.key;
              return (
                <button key={tab.key} onClick={() => handleTabClick(tab.key)}
                  style={{ padding: '7px 16px', fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? '#1a1a1a' : '#5f6368', background: isActive ? '#ffffff' : 'transparent', border: isActive ? '1px solid #e0e0e0' : '1px solid transparent', borderBottom: isActive ? '1px solid #ffffff' : '1px solid transparent', borderRadius: '8px 8px 0 0', cursor: 'pointer', marginBottom: isActive ? -1 : 0, position: 'relative', zIndex: isActive ? 1 : 0, whiteSpace: 'nowrap', transition: 'background 0.15s' }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#e8eaed'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  {tab.label}
                  {loading && activeTab === tab.key && (
                    <RefreshCw className="inline h-3 w-3 ml-1 animate-spin" style={{ verticalAlign: 'middle' }} />
                  )}
                </button>
              );
            })}

            {/* Separator */}
            <div style={{ width: 1, height: 20, background: '#d0d0d0', margin: '0 4px 4px' }} />
            <span style={{ fontSize: 10, color: '#9aa0a8', marginBottom: 6, marginRight: 2, whiteSpace: 'nowrap' }}>Analysis</span>

            {REASONING_TABS.map(tab => {
              const isActive = activeTab === tab.key;
              return (
                <button key={tab.key} onClick={() => handleTabClick(tab.key)}
                  style={{ padding: '7px 14px', fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? '#1a1a1a' : '#7c3aed', background: isActive ? '#ffffff' : 'transparent', border: isActive ? '1px solid #e0e0e0' : '1px solid transparent', borderBottom: isActive ? '1px solid #ffffff' : '1px solid transparent', borderRadius: '8px 8px 0 0', cursor: 'pointer', marginBottom: isActive ? -1 : 0, position: 'relative', zIndex: isActive ? 1 : 0, whiteSpace: 'nowrap', transition: 'background 0.15s' }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#ede9fe'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  {tab.label}
                  {loading && activeTab === tab.key && (
                    <RefreshCw className="inline h-3 w-3 ml-1 animate-spin" style={{ verticalAlign: 'middle' }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Narrative content */}
          <div className="px-5 py-4">
            {loading ? (
              <div className="text-center text-sm text-gray-400 py-4">
                <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2 text-purple-400" />
                Generating briefing…
              </div>
            ) : activeText ? (
              <div className="flex gap-3">
                <p className="flex-1 text-sm text-gray-700 leading-relaxed">{activeText}</p>
                <div className="flex flex-col items-start gap-1 flex-shrink-0">
                  <CopyButton text={activeText} />
                  <ListenButton text={activeText} />
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">
                Narrative unavailable for this view. Click Regenerate to retry.
              </p>
            )}
          </div>

          {/* Disclaimer */}
          <AiDisclaimer />
        </>
      )}
    </div>
  );
};

export default RansomwareNarrativePanel;
